// =============================================================================
// Template Data Module — unified template data loading, caching, retry, dedupe
// =============================================================================
// Responsibilities: fetch, cache, retry, in-flight dedupe, field normalization,
// CSS merging, CSS variable extraction, renderPages generation.
// Does NOT: render DOM, create iframes, decide pagination, call TemplateRenderer.

const CACHE_PREFIX = 'template-data:v2:';

const TTL = {
  officialRegistry: 10 * 60 * 1000,
  communityList: 0,
  preview: 5 * 60 * 1000,
  singleTemplate: 10 * 60 * 1000,
};

const INVALIDATE_KEY = CACHE_PREFIX + 'invalidate:community:v1';

// ---- internal state ---------------------------------------------------------

const _memoryCache = new Map();   // key -> { status, data, promise, updatedAt }

function _cacheKey(type, id) {
  return type + '::' + (id || '_');
}

// ---- helpers ----------------------------------------------------------------

function _readJSON(key) {
  try {
    var raw = sessionStorage.getItem(CACHE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

function _writeJSON(key, value) {
  try { sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value)); } catch (e) {}
}

function _removeJSON(key) {
  try { sessionStorage.removeItem(CACHE_PREFIX + key); } catch (e) {}
}

function _delay(ms) {
  return new Promise(function (resolve) { setTimeout(resolve, ms); });
}

function _jitter(baseMs) {
  return baseMs + Math.floor(Math.random() * baseMs * 0.3);
}

// ---- retry-enabled fetch ----------------------------------------------------

function _fetchJSONWithRetry(url, options, retriesLeft) {
  if (retriesLeft === undefined) retriesLeft = 2;
  return fetch(url, options || {}).then(function (r) {
    if (!r.ok) {
      var err = new Error('HTTP ' + r.status);
      err.status = r.status;
      throw err;
    }
    return r.json();
  }).catch(function (err) {
    if (retriesLeft <= 0) throw err;
    // 401/403 — don't retry
    if (err.status === 401 || err.status === 403) throw err;
    // 404 for some paths — don't retry (handled per-call)
    var wait = _jitter(err.status === 429 ? 900 : 250);
    return _delay(wait).then(function () {
      return _fetchJSONWithRetry(url, options, retriesLeft - 1);
    });
  });
}

// ---- in-flight dedupe -------------------------------------------------------

function _dedupe(key, fetcher) {
  var entry = _memoryCache.get(key);
  if (entry && entry.promise) return entry.promise;

  var promise = fetcher().then(function (result) {
    _memoryCache.set(key, { status: result.status, data: result.data, updatedAt: Date.now() });
    return result;
  }).catch(function (err) {
    _memoryCache.set(key, { status: 'error', data: null, error: err, updatedAt: Date.now() });
    throw err;
  }).finally(function () {
    if (_memoryCache.get(key)) _memoryCache.get(key).promise = null;
  });

  _memoryCache.set(key, { status: 'loading', data: null, promise: promise, updatedAt: Date.now() });
  return promise;
}

// ---- stale cache helpers ----------------------------------------------------

function _staleResult(data) {
  return { status: 'stale', data: data };
}

function _readyResult(data) {
  return { status: 'ready', data: data };
}

function _errorResult(err) {
  var e = err || new Error('Template data unavailable');
  e.state = 'error';
  return { status: 'error', data: null, error: e };
}

function _isExpired(updatedAt, ttlMs) {
  return !updatedAt || (Date.now() - updatedAt) > ttlMs;
}

// ---- sessionStorage cache ---------------------------------------------------

function _loadFromSession(key) {
  var entry = _readJSON(key);
  if (!entry) return null;
  // migration: ignore stale session data older than 30min regardless of TTL
  if (Date.now() - entry._savedAt > 30 * 60 * 1000) {
    _removeJSON(key);
    return null;
  }
  return entry;
}

function _saveToSession(key, data) {
  _writeJSON(key, Object.assign({}, data, { _savedAt: Date.now() }));
}

// ---- community invalidation -------------------------------------------------

var _lastSeenInvalidationAt = (function () {
  var token = _readJSON('invalidate:community:v1');
  return token ? token.at || 0 : 0;
})();

function _checkAndApplyInvalidation() {
  var token = _readJSON('invalidate:community:v1');
  if (!token) return;
  if (token.at <= _lastSeenInvalidationAt) return;
  _lastSeenInvalidationAt = token.at;

  // Clear community list memory cache
  var listKey = _cacheKey('communityList', 'default');
  _memoryCache.delete(listKey);

  // Clear all preview memory caches (we don't know which ids were affected)
  _memoryCache.forEach(function (_, key) {
    if (key.startsWith('preview::')) _memoryCache.delete(key);
  });

  // Clear sessionStorage stale caches
  var allKeys = [];
  try {
    for (var i = 0; i < sessionStorage.length; i++) {
      var k = sessionStorage.key(i);
      if (k && k.indexOf(CACHE_PREFIX + 'community:list:') === 0) allKeys.push(k);
      if (k && k.indexOf(CACHE_PREFIX + 'published-preview:') === 0) allKeys.push(k);
    }
  } catch (e) {}
  allKeys.forEach(function (k) {
    try { sessionStorage.removeItem(k); } catch (e) {}
  });
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Load official template registry.
 * @param {Object} options
 * @param {boolean} [options.includeHtml=false] — include html_content in templates
 * @returns {Promise<{status:string, data:{styles,albums,templates}}>}
 */
export async function getOfficialRegistry(options) {
  options = options || {};
  var includeHtml = options.includeHtml !== undefined ? options.includeHtml : false;
  var key = _cacheKey('officialRegistry', includeHtml ? 'withHtml' : 'noHtml');

  // Check memory cache (fresh)
  var mem = _memoryCache.get(key);
  if (mem && mem.status === 'ready' && !_isExpired(mem.updatedAt, TTL.officialRegistry)) {
    return _readyResult(mem.data);
  }

  return _dedupe(key, function () {
    return _fetchJSONWithRetry('/api/templates?include_html=' + (includeHtml ? 'true' : 'false'), { credentials: 'include' })
      .then(function (data) {
        if (!data || !data.templates || !data.albums) throw new Error('Invalid template registry');

        // Normalize to backwards-compatible shape
        var registry = { styles: data.styles, albums: data.albums, templates: [] };
        data.templates.forEach(function (t) {
          var entry = {
            id: t.id, name: t.name, style: t.style, useCase: t.useCase || '', category: t.category,
            album: t.album, file: t.file, css_path: t.css_path,
            baseSize: t.pageBaseSize, pageSize: t.pageSize,
            renderClass: t.renderClass || '', defaultFor: t.defaultFor,
            _isBackCover: t.is_back_cover || false
          };
          if (includeHtml) entry.html_content = t.html_content;
          registry.templates.push(entry);
        });

        // Persist to sessionStorage
        _saveToSession('official-registry:' + (includeHtml ? 'withHtml' : 'noHtml'), registry);

        return _readyResult(registry);
      })
      .catch(function (err) {
        // Try sessionStorage fallback
        var cached = _loadFromSession('official-registry:' + (includeHtml ? 'withHtml' : 'noHtml'));
        if (cached && cached.templates && cached.albums) {
          return _staleResult(cached);
        }
        throw err;
      });
  });
}

/**
 * Load community template list.
 * @param {Object} options
 * @param {string} [options.category]
 * @param {number} [options.page]
 * @param {number} [options.pageSize]
 * @returns {Promise<{status:string, data:{templates,total,page,pageSize}}>}
 */
export async function getCommunityTemplates(options) {
  options = options || {};
  _checkAndApplyInvalidation();

  var key = _cacheKey('communityList', 'default');

  // Check memory cache (fresh)
  var mem = _memoryCache.get(key);
  if (mem && mem.status === 'ready' && !_isExpired(mem.updatedAt, TTL.communityList)) {
    return _readyResult(mem.data);
  }

  return _dedupe(key, function () {
    var params = new URLSearchParams();
    if (options.category && options.category !== '全部') params.set('category', options.category);
    if (options.pageSize) params.set('page_size', options.pageSize);
    if (options.page) params.set('page', options.page);

    var url = '/api/templates/marketplace';
    if (params.toString()) url += '?' + params.toString();

    return _fetchJSONWithRetry(url, { cache: 'no-store' })
      .then(function (data) {
        var result = {
          templates: data.templates || [],
          total: data.total || 0,
          page: data.page || 1,
          pageSize: data.page_size || (data.templates ? data.templates.length : 0)
        };
        _saveToSession('community:list:default', result);
        return _readyResult(result);
      })
      .catch(function (err) {
        var cached = _loadFromSession('community:list:default');
        if (cached && cached.templates) return _staleResult(cached);
        throw err;
      });
  });
}

// ---- CSS variable extraction ------------------------------------------------

function _extractCSSVar(css, varName, fallback) {
  var pattern = varName.replace(/-/g, '\\-') + '\\s*:\\s*';
  var idx = css.search(new RegExp(pattern));
  if (idx === -1) return fallback;
  var start = idx + css.match(new RegExp(pattern))[0].length;
  var depth = 0;
  var end = start;
  while (end < css.length) {
    var ch = css[end];
    if (ch === '(' || ch === '{' || ch === '[') depth++;
    else if (ch === ')' || ch === '}' || ch === ']') depth--;
    else if (depth === 0 && (ch === ';' || ch === '}')) break;
    end++;
  }
  return css.substring(start, end).trim() || fallback;
}

// ---- community preview helpers ----------------------------------------------

function _normalizeSize(size, fallback) {
  fallback = fallback || { width: 600, height: 600 };
  if (typeof size === 'number') return { width: size, height: size };
  if (size && typeof size.width === 'number' && typeof size.height === 'number') {
    return { width: size.width, height: size.height };
  }
  return { width: fallback.width || 600, height: fallback.height || 600 };
}

function _buildRenderPage(pageData, templateCss) {
  var fullSize = _normalizeSize(pageData.page_size, { width: 600, height: 600 });
  var baseSize = pageData.page_base_size || fullSize.width || 600;
  var renderSize = fullSize;
  var spreadHalf = null;
  var isSpreadTemplate = pageData.is_unified_spread || pageData.template_category === '双页';

  if (isSpreadTemplate && fullSize.width > fullSize.height) {
    renderSize = { width: Math.round(fullSize.width / 2), height: fullSize.height };
    baseSize = renderSize.width;
    spreadHalf = pageData.is_back_cover ? 'right' : (pageData.side === 'right' ? 'right' : 'left');
  }

  var out = {
    html: pageData.html_content || '',
    htmlKind: 'saved-page',
    templateId: pageData.template_id,
    baseSize: baseSize,
    pageSize: renderSize,
    renderClass: pageData.render_class || '',
    _cssOverride: templateCss[pageData.template_id] || ''
  };
  if (spreadHalf) out._spreadHalf = spreadHalf;
  if (pageData.is_cover) out._isCover = true;
  if (pageData.is_back_cover) out._isBackCover = true;
  return out;
}

/**
 * Load published template preview.
 * @param {string|number} templateId
 * @param {Object} options
 * @returns {Promise<{status:string, data:{id,name,source_album,pages,template_css,cssText,theme,renderPages}}>}
 */
export async function getPublishedTemplatePreview(templateId, options) {
  _checkAndApplyInvalidation();

  var key = _cacheKey('preview', String(templateId));

  var mem = _memoryCache.get(key);
  if (mem && mem.status === 'ready' && !_isExpired(mem.updatedAt, TTL.preview)) {
    return _readyResult(mem.data);
  }

  return _dedupe(key, function () {
    return _fetchJSONWithRetry('/api/templates/published/' + encodeURIComponent(templateId) + '/preview', { credentials: 'include' }, 2)
      .then(function (data) {
        var templateCss = (data && data.template_css) || {};
        var pages = data.pages || [];

        // Merge CSS
        var allCss = '';
        Object.keys(templateCss).forEach(function (k) {
          allCss += templateCss[k] + '\n';
        });

        // Extract theme
        var coverColor = _extractCSSVar(allCss, '--bg-warm', '') || _extractCSSVar(allCss, '--bg', '') || _extractCSSVar(allCss, '--bg-light', '') || '#e0e5ec';
        var accentColor = _extractCSSVar(allCss, '--accent', '') || _extractCSSVar(allCss, '--accent1', '') || '#6c8ebf';

        // Build renderPages ordered by spread_index / side
        var spreadMap = {};
        pages.forEach(function (p) {
          var idx = p.spread_index;
          if (!spreadMap[idx]) spreadMap[idx] = {};
          spreadMap[idx][p.side || 'left'] = p;
        });

        var pageDataList = [];
        var spreadIndices = Object.keys(spreadMap).sort(function (a, b) { return a - b; });
        spreadIndices.forEach(function (idx) {
          var spread = spreadMap[idx];
          if (spread.left) pageDataList.push(_buildRenderPage(spread.left, templateCss));
          if (spread.right) pageDataList.push(_buildRenderPage(spread.right, templateCss));
        });

        var result = {
          id: data.id,
          name: data.name,
          source_album: data.source_album,
          pages: pages,
          template_css: templateCss,
          cssText: allCss,
          theme: { coverColor: coverColor, accentColor: accentColor },
          renderPages: pageDataList
        };

        _saveToSession('published-preview:' + templateId, result);

        return _readyResult(result);
      })
      .catch(function (err) {
        // 404 — don't retry, try stale cache
        var cached = _loadFromSession('published-preview:' + templateId);
        if (cached && cached.renderPages) return _staleResult(cached);
        throw err;
      });
  });
}

/**
 * Load official theme template list.
 * @param {Object} options
 * @param {string} [options.category]
 * @returns {Promise<{status:string, data:{templates:Array,total:number}}>}
 */
export async function getOfficialThemeTemplates(options) {
  options = options || {};
  var key = _cacheKey('officialThemeList', options.category || 'all');

  var mem = _memoryCache.get(key);
  if (mem && mem.status === 'ready' && !_isExpired(mem.updatedAt, TTL.communityList)) {
    return _readyResult(mem.data);
  }

  return _dedupe(key, function () {
    var params = new URLSearchParams();
    if (options.category && options.category !== '全部') params.set('category', options.category);
    var url = '/api/templates/official-themes';
    if (params.toString()) url += '?' + params.toString();

    return _fetchJSONWithRetry(url, { cache: 'no-store' })
      .then(function (data) {
        var result = { templates: data || [], total: (data || []).length };
        _saveToSession('official-theme-list:' + (options.category || 'all'), result);
        return _readyResult(result);
      })
      .catch(function (err) {
        var cached = _loadFromSession('official-theme-list:' + (options.category || 'all'));
        if (cached && cached.templates) return _staleResult(cached);
        throw err;
      });
  });
}

/**
 * Load official theme template preview.
 * @param {string|number} templateId
 * @param {Object} options
 * @returns {Promise<{status:string, data:{id,name,source,badge,pages,template_css,cssText,theme,renderPages}}>}
 */
export async function getOfficialThemeTemplatePreview(templateId, options) {
  var key = _cacheKey('officialThemePreview', String(templateId));

  var mem = _memoryCache.get(key);
  if (mem && mem.status === 'ready' && !_isExpired(mem.updatedAt, TTL.preview)) {
    return _readyResult(mem.data);
  }

  return _dedupe(key, function () {
    return _fetchJSONWithRetry('/api/templates/official-themes/' + encodeURIComponent(templateId) + '/preview', { credentials: 'include' }, 2)
      .then(function (data) {
        var templateCss = (data && data.template_css) || {};
        var pages = data.pages || [];

        // Merge CSS
        var allCss = '';
        Object.keys(templateCss).forEach(function (k) {
          allCss += templateCss[k] + '\n';
        });

        // Extract theme
        var coverColor = _extractCSSVar(allCss, '--bg-warm', '') || _extractCSSVar(allCss, '--bg', '') || _extractCSSVar(allCss, '--bg-light', '') || '#e0e5ec';
        var accentColor = _extractCSSVar(allCss, '--accent', '') || _extractCSSVar(allCss, '--accent1', '') || '#6c8ebf';

        // Build renderPages ordered by spread_index / side
        var spreadMap = {};
        pages.forEach(function (p) {
          var idx = p.spread_index;
          if (!spreadMap[idx]) spreadMap[idx] = {};
          spreadMap[idx][p.side || 'left'] = p;
        });

        var pageDataList = [];
        var spreadIndices = Object.keys(spreadMap).sort(function (a, b) { return a - b; });
        spreadIndices.forEach(function (idx) {
          var spread = spreadMap[idx];
          if (spread.left) pageDataList.push(_buildRenderPage(spread.left, templateCss));
          if (spread.right) pageDataList.push(_buildRenderPage(spread.right, templateCss));
        });

        var result = {
          id: data.id,
          name: data.name,
          source: 'official_theme',
          badge: '官方 · 主题',
          pages: pages,
          template_css: templateCss,
          cssText: allCss,
          theme: { coverColor: coverColor, accentColor: accentColor },
          renderPages: pageDataList
        };

        _saveToSession('official-theme-preview:' + templateId, result);
        return _readyResult(result);
      })
      .catch(function (err) {
        var cached = _loadFromSession('official-theme-preview:' + templateId);
        if (cached && cached.renderPages) return _staleResult(cached);
        throw err;
      });
  });
}

/**
 * Load a single official template HTML.
 * @param {string} templateId
 * @param {Object} options
 * @returns {Promise<{status:string, data:Object}>}
 */
export async function getTemplateById(templateId, options) {
  var key = _cacheKey('singleTemplate', templateId);

  var mem = _memoryCache.get(key);
  if (mem && mem.status === 'ready' && !_isExpired(mem.updatedAt, TTL.singleTemplate)) {
    return _readyResult(mem.data);
  }

  return _dedupe(key, function () {
    return _fetchJSONWithRetry('/api/templates/' + encodeURIComponent(templateId), { cache: 'no-store' })
      .then(function (data) {
        _saveToSession('single-template:' + templateId, data);
        return _readyResult(data);
      })
      .catch(function (err) {
        var cached = _loadFromSession('single-template:' + templateId);
        if (cached) return _staleResult(cached);
        throw err;
      });
  });
}

/**
 * Notify the module that template data has changed (publish/unpublish/delete).
 * Call this from dashboard after publishing, unpublishing, or deleting a community template.
 * @param {{scope:string, templateIds:string[], at:number}} change
 */
export function markTemplateDataChanged(change) {
  if (!change || !change.scope) return;
  _writeJSON('invalidate:community:v1', { at: change.at || Date.now(), scope: change.scope, templateIds: change.templateIds || [] });
  _lastSeenInvalidationAt = change.at || Date.now();

  // Immediate memory cache cleanup
  _memoryCache.delete(_cacheKey('communityList', 'default'));
  if (change.templateIds) {
    change.templateIds.forEach(function (id) {
      _memoryCache.delete(_cacheKey('preview', String(id)));
      _removeJSON('published-preview:' + id);
    });
  }
  _removeJSON('community:list:default');
}

/**
 * Clear all in-memory caches. Does NOT clear sessionStorage.
 */
export function clearTemplateDataMemoryCache() {
  _memoryCache.clear();
}

/**
 * Return internal state for debugging.
 */
export function getTemplateDataState() {
  var entries = [];
  _memoryCache.forEach(function (val, key) {
    entries.push({ key: key, status: val.status, updatedAt: val.updatedAt, hasData: !!val.data });
  });
  return { memoryEntries: entries, lastInvalidationAt: _lastSeenInvalidationAt };
}
